import {
  EpiForms,
  EpiFormsFormDescriptor
} from "../../Shared/Assets/src/types/epi-forms";

// EPiServer.Forms.Extension.onBeforeSubmit runs *before* Forms validates the current step, and
// Forms has no awaited hook after validation (_doSubmitForm goes straight to the Submit XHR), so
// to capture consent exactly once - only when Forms is actually about to submit - we have to
// predict Forms' own decision. This mirrors EPiServerForms.js (5.8.2) using the same public
// services it uses itself, rather than approximating it (form.checkValidity() did that, and
// silently skipped consent capture whenever a native constraint Forms ignores failed, e.g. the
// visible phone input's pattern="[0-9]*" for a number typed with spaces).
export type FormsSubmitPrediction =
  // Forms will validate this step successfully and send a finalised submission.
  | { kind: "accepted" }
  // Forms will move to another step (or post a non-final step) - consent is captured on the
  // finalising submit only, so a multi-step form doesn't call /consents on every "Next".
  | { kind: "not-final" }
  // Forms will block the submit and show its own validation errors. failedValidators: the short
  // type names of the validators that failed (e.g. OneTrustContactRequiredValidator), for telemetry.
  | { kind: "rejected"; failedValidators: string[] }
  // The Forms client API isn't as expected (e.g. changed in a Forms upgrade), so Forms' decision
  // can't be predicted.
  | { kind: "unknown"; reason: string };

const SUBMIT_ACTION_SELECTOR =
  "[data-f-type='submitbutton'], .FormSubmitButton, [data-f-navigation-next], .btnNext";

// The button whose click triggered the current submit attempt, per form - onBeforeSubmit only
// receives the form, but Forms' finalisation decision also depends on the clicked button (see
// isFinalisingSubmit). Forms invokes onBeforeSubmit synchronously from that button's click
// handler, so a capture-phase listener has always recorded it by then, and onBeforeSubmit takes
// it (see takeSubmitAction) before doing anything asynchronous.
//
// Not every submit comes from a native click: on Enter in a step without a visible submit button,
// Forms runs the Next button's handler via jQuery's trigger("click"), which (as that handler
// prevents the default) never dispatches a click. Taking the entry means such a submit finds none,
// rather than an earlier attempt's button - e.g. a finalising submit button that Forms rejected,
// which would otherwise make that "Next" look like a finalising submit.
const lastSubmitAction = new WeakMap<HTMLFormElement, HTMLElement>();

export const trackSubmitActions = (): void => {
  document.addEventListener(
    "click",
    event => {
      const action =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>(SUBMIT_ACTION_SELECTOR)
          : null;
      const form = action?.closest("form");

      if (action && form) {
        lastSubmitAction.set(form, action);
      }
    },
    true
  );
};

// Returns and forgets the button recorded for this form's current submit attempt, if any. Called
// synchronously at the start of onBeforeSubmit, so it's always the attempt Forms is running.
export const takeSubmitAction = (form: HTMLFormElement): HTMLElement | undefined => {
  const action = lastSubmitAction.get(form);

  lastSubmitAction.delete(form);

  return action;
};

const isFormsApiAvailable = (forms: EpiForms | undefined): forms is EpiForms =>
  typeof forms?.Utils?.getCurrentStepIndex === "function" &&
  typeof forms.Utils.getElementName === "function" &&
  typeof forms.Utils.getElementValue === "function" &&
  typeof forms.Validation?.getElementValidators === "function" &&
  typeof forms.Validation.validateFormValue === "function" &&
  typeof forms.Navigation?.findNextStep === "function" &&
  typeof forms.Extension?.getAllDataElements === "function";

// Mirrors _doSubmitForm's isFinalizedSubmission.
const isFinalisingSubmit = (
  forms: EpiForms,
  descriptor: EpiFormsFormDescriptor,
  currentStepIndex: number,
  action: HTMLElement | undefined
): boolean => {
  const stepCount = descriptor.StepsInfo?.Steps.length ?? 1;
  const isLastStep = currentStepIndex === stepCount - 1;
  const nextStep = forms.Navigation.findNextStep(currentStepIndex, descriptor);

  const isFinalizedButton =
    action?.dataset.fType === "submitbutton" &&
    (action.dataset.fIsFinalized === "true" ||
      action.dataset.epiformsIsFinalized === "true");

  return (nextStep ? false : isLastStep) || isFinalizedButton;
};

// Mirrors validateElementsIn/validateElement for the current step, without their UI updates
// (Forms runs those itself straight after onBeforeSubmit resolves). Returns the short type names
// of the validators that would fail - none means Forms would accept the step.
const getFailedValidators = (
  stepElement: Element,
  forms: EpiForms,
  descriptor: EpiFormsFormDescriptor,
  $: NonNullable<Window["$$epiforms"]>
): string[] =>
  forms.Extension.getAllDataElements($(stepElement))
    .toArray()
    .flatMap((element: HTMLElement) => {
      const $element = $(element);
      const elementName = forms.Utils.getElementName($element) ?? "";
      const validators = forms.Validation.getElementValidators(
        descriptor.ValidationInfo,
        element.id || elementName
      );

      if (
        (descriptor.DependencyInactiveElements ?? []).includes(elementName) ||
        !Array.isArray(validators) ||
        validators.length === 0
      ) {
        return [];
      }

      const results = forms.Validation.validateFormValue(
        elementName,
        forms.Utils.getElementValue($element),
        validators
      );

      // validateFormValue returns one result per validator, in order - unless it skipped one with
      // no validate function, when the failures can no longer be told apart.
      if (results.length !== validators.length) {
        return results.some(result => result.isValid === false) ? ["Unknown"] : [];
      }

      return validators
        .filter((_, index) => results[index].isValid === false)
        .map(validator => validator.type.split(".").pop() || "Unknown");
    });

// submitAction: the button that triggered this attempt (see takeSubmitAction), if it was a click.
export const predictFormsSubmit = (
  form: HTMLFormElement,
  submitAction: HTMLElement | undefined
): FormsSubmitPrediction => {
  const forms = window.epi?.EPiServer?.Forms;
  const $ = window.$$epiforms;

  if (!isFormsApiAvailable(forms) || !$) {
    return { kind: "unknown", reason: "EPiServer Forms client API not available" };
  }

  const descriptor = forms[form.id] as EpiFormsFormDescriptor | undefined;

  if (!descriptor?.ValidationInfo) {
    return { kind: "unknown", reason: `No Forms descriptor for form "${form.id}"` };
  }

  let stepElement: Element;

  try {
    const currentStepIndex = forms.Utils.getCurrentStepIndex(descriptor);

    if (!isFinalisingSubmit(forms, descriptor, currentStepIndex, submitAction)) {
      return { kind: "not-final" };
    }

    stepElement = descriptor.$steps?.[currentStepIndex] ?? form;
  } catch (e) {
    return { kind: "unknown", reason: `Couldn't resolve the current step: ${String(e)}` };
  }

  try {
    const failedValidators = getFailedValidators(stepElement, forms, descriptor, $);

    return failedValidators.length === 0
      ? { kind: "accepted" }
      : { kind: "rejected", failedValidators: [...new Set(failedValidators)] };
  } catch {
    // Forms' own validation runs these same validators and would hit the same exception
    // (e.g. a validator that isn't registered yet), which stops it from submitting.
    return { kind: "rejected", failedValidators: ["ValidatorThrew"] };
  }
};
